import React, { useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import FormNumericTextField from "../../../components/formFields/FormNumericTextField";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import "../b2bmodule.scss";

interface PriceBreakdownDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any, syncAll: boolean) => void;
  dateLabel: string;
  nextDateLabel?: string;
  initialValues: {
    AdultPrice: number;
    AWEBPrice: number;
    CWEBPrice: number;
    CNBPrice: number;
  };
  quantities: {
    rooms: number;
    aweb: number;
    cweb: number;
    cnb: number;
  };
}

const PriceBreakdownDialog: React.FC<PriceBreakdownDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  dateLabel,
  nextDateLabel,
  initialValues,
  quantities
}) => {
  const [syncAll, setSyncAll] = useState(false);

  // Reset syncAll when dialog opens/props change
  React.useEffect(() => {
    if (isOpen) setSyncAll(false);
  }, [isOpen, dateLabel]);

  if (!isOpen) return null;

  return (
    <Dialog
      title={(
        <div className="d-flex justify-content-between align-items-center w-100 pe-3">
          <span className="fw-bold">Given Price - {dateLabel}</span>
        </div>
      )}
      onClose={onClose}
      width={500}
      className="price-breakdown-dialog"
    >
      <Form
        initialValues={initialValues}
        onSubmit={(data: any) => {
          const roomPriceNum = Number(data.AdultPrice) || 0;
          const awebPriceNum = Number(data.AWEBPrice) || 0;
          const cwebPriceNum = Number(data.CWEBPrice) || 0;
          const cnbPriceNum = Number(data.CNBPrice) || 0;

          const roomTotal = roomPriceNum * (Number(quantities.rooms) || 0);
          const awebTotal = awebPriceNum * (Number(quantities.aweb) || 0);
          const cwebTotal = cwebPriceNum * (Number(quantities.cweb) || 0);
          const cnbTotal = cnbPriceNum * (Number(quantities.cnb) || 0);

          const grandTotal = roomTotal + awebTotal + cwebTotal + cnbTotal;

          const dataToSave = {
            AdultPrice: roomPriceNum,
            AWEBPrice: awebPriceNum,
            CWEBPrice: cwebPriceNum,
            CNBPrice: cnbPriceNum,
            total: grandTotal
          };
          onSave(dataToSave, syncAll);
        }}
        render={(formRenderProps: any) => {
          const roomPriceNum = Number(formRenderProps.valueGetter("AdultPrice")) || 0;
          const awebPriceNum = Number(formRenderProps.valueGetter("AWEBPrice")) || 0;
          const cwebPriceNum = Number(formRenderProps.valueGetter("CWEBPrice")) || 0;
          const cnbPriceNum = Number(formRenderProps.valueGetter("CNBPrice")) || 0;

          const roomTotal = roomPriceNum * (Number(quantities.rooms) || 0);
          const awebTotal = awebPriceNum * (Number(quantities.aweb) || 0);
          const cwebTotal = cwebPriceNum * (Number(quantities.cweb) || 0);
          const cnbTotal = cnbPriceNum * (Number(quantities.cnb) || 0);

          const grandTotal = roomTotal + awebTotal + cwebTotal + cnbTotal;

          return (
            <FormElement>
              <div className="p-3">
                <table className="breakdown-table w-100 border rounded mb-3">
                  <thead>
                    <tr className="bg-light">
                      <th className="p-2 border-bottom"></th>
                      <th className="p-2 border-bottom text-center">Price (INR)</th>
                      <th className="p-2 border-bottom text-center">Quantity</th>
                      <th className="p-2 border-bottom text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 border-bottom fw-bold">/Room (2P)</td>
                      <td className="p-2 border-bottom" style={{ width: "120px" }}>
                        <Field name="AdultPrice" component={FormNumericTextField} min={0} />
                      </td>
                      <td className="p-2 border-bottom text-center">{quantities.rooms || "-"}</td>
                      <td className="p-2 border-bottom text-end fw-semibold">{quantities.rooms ? roomTotal.toLocaleString() : "-"}</td>
                    </tr>
                    <tr>
                      <td className="p-2 border-bottom fw-bold">/AWEB</td>
                      <td className="p-2 border-bottom">
                        <Field name="AWEBPrice" component={FormNumericTextField} min={0} />
                      </td>
                      <td className="p-2 border-bottom text-center">{quantities.aweb || "-"}</td>
                      <td className="p-2 border-bottom text-end fw-semibold">{quantities.aweb ? awebTotal.toLocaleString() : "-"}</td>
                    </tr>
                    <tr>
                      <td className="p-2 border-bottom fw-bold">/CWEB</td>
                      <td className="p-2 border-bottom">
                        <Field name="CWEBPrice" component={FormNumericTextField} min={0} />
                      </td>
                      <td className="p-2 border-bottom text-center">{quantities.cweb || "-"}</td>
                      <td className="p-2 border-bottom text-end fw-semibold">{quantities.cweb ? cwebTotal.toLocaleString() : "-"}</td>
                    </tr>
                    <tr>
                      <td className="p-2 border-bottom fw-bold">/CNB</td>
                      <td className="p-2 border-bottom">
                        <Field name="CNBPrice" component={FormNumericTextField} min={0} />
                      </td>
                      <td className="p-2 border-bottom text-center">{quantities.cnb || "-"}</td>
                      <td className="p-2 border-bottom text-end fw-semibold">{quantities.cnb ? cnbTotal.toLocaleString() : "-"}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr className="bg-white">
                      <td colSpan={3} className="p-2 text-end fw-bold">Total</td>
                      <td className="p-2 text-end fw-bold text-primary h5 m-0">
                        <small className="text-muted pe-1" style={{ fontSize: "10px" }}>INR</small>
                        {grandTotal.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>

                <div className="mb-4">
                  <Checkbox
                    label={`Keep same prices for other nights${nextDateLabel ? `: ${nextDateLabel}` : ''}`}
                    value={syncAll}
                    onChange={(e) => setSyncAll(e.value)}
                  />
                </div>

                <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-3">
                  <Button type="button" themeColor="primary" onClick={formRenderProps.onSubmit} className="px-5 fw-bold" style={{ borderRadius: "8px" }}>Save</Button>
                  <Button type="button" fillMode="outline" onClick={onClose} className="px-4">Cancel</Button>
                </div>
              </div>
            </FormElement>
          );
        }}
      />
    </Dialog>
  );
};

export default PriceBreakdownDialog;
